import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { Action } from '../../types/toolbar';
import { Toolbar } from '../../components/ui/toolbar/toolbar.component';
import CustomModal from '../../components/ui/modal/modal.component';
import JobFormRuleForm from '../../components/ui/jobFormRuleForm/jobFormRuleForm.component';
import { useCRUD } from '../../api/crud';
import { JOB_FORM_RULES_ENDPOINT, SUCCESS_SNACKBAR, ERROR_SNACKBAR } from '../../consts';
import { useHasPermission } from '../../contexts/auth';
import { TENANT_ROUTES } from '../../workspaces/tenantConfig/tenantConfig.routes';

export const TenantJobFormRuleActions = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { loading, remove } = useCRUD(JOB_FORM_RULES_ENDPOINT);
  const [editOpened, setEditOpened] = useState<boolean>(false);
  const [deleteOpened, setDeleteOpened] = useState<boolean>(false);
  const canChange = useHasPermission('service.change_jobformrule');
  const canDelete = useHasPermission('service.delete_jobformrule');

  const handleDelete = async () => {
    if (loading || !id) return;
    const result = await remove({ id });
    if (result?.success) {
      enqueueSnackbar(t('Job form rule deleted successfully'), SUCCESS_SNACKBAR);
      setDeleteOpened(false);
      window.dispatchEvent(new Event('jobFormRulesUpdated'));
      navigate(TENANT_ROUTES.jobFormRules);
    } else {
      enqueueSnackbar(t('Job form rule deletion failed'), ERROR_SNACKBAR);
    }
  };

  const actions: Action[] = [
    {
      icon: <EditIcon />,
      label: t('Edit job form rule'),
      onClick: () => setEditOpened(true),
      disabled: !canChange,
    },
    {
      icon: <DeleteIcon />,
      label: t('Delete job form rule'),
      onClick: () => setDeleteOpened(true),
      disabled: !canDelete,
      color: 'var(--errorColor)',
    },
  ];

  return (
    <>
      <Toolbar actions={actions} />
      <CustomModal
        opened={editOpened}
        setOpened={setEditOpened}
        title={t('Edit job form rule')}
        content={
          <JobFormRuleForm
            id={id}
            onSuccess={() => {
              setEditOpened(false);
              window.dispatchEvent(new Event('jobFormRulesUpdated'));
            }}
          />
        }
      />
      <CustomModal
        opened={deleteOpened}
        setOpened={setDeleteOpened}
        title={t('Delete job form rule')}
        content={
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t('Jobs with these services will no longer require the rule\'s fields.')}
            </Typography>
            <Alert severity="warning">
              <Typography variant="body2">{t('This action cannot be undone.')}</Typography>
            </Alert>
            <Button variant="contained" color="error" onClick={handleDelete} disabled={loading}>
              {loading ? t('Processing...') : t('Confirm deletion')}
            </Button>
          </Box>
        }
      />
    </>
  );
};
